import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { terms } from '../../../language/terms';
import { customElement, property, state } from 'lit/decorators.js';
import styles from './state-colours-page.styles.css?inline';
import { router, resolveRouterPath } from '../../../router';
import {
  RETIRED_STATE_DOMAINS,
  STATE_DOMAINS,
  STATE_LABELS,
  STATE_PALETTE_FAMILIES,
  resolveState,
  type StateDomainKey,
  type StateLabelKey,
  type StatePaletteColour,
} from '../../../utility/state-colours/state-colour-registry';
import {
  getLabelOverrideColour,
  getLabelOverrideRow,
  getUseOverrideRow,
  setStateColourOverrides,
  subscribeStateColourOverrides,
} from '../../../utility/state-colours/state-colour-overrides-store';
import {
  ensureStateColourOverridesLoaded,
  reloadStateColourOverrides,
} from '../../../utility/state-colours/state-colour-overrides-loader';
import { getStateColoursApi } from '../../../api/state-colours-api';
import { activeUserProfile } from '../../../active-userprofile';
import { CommonRoleId } from '../../../utility/claim-id';
import type { StateColourPickTarget } from './state-colour-picker-dialog';
import type { StateColourPickerDialog } from './state-colour-picker-dialog';
import './state-colour-picker-dialog';
import '../../../components/state-badge/agwa-state-badge';
import '../../../components/section/agwa-section';
import '@shoelace-style/shoelace/dist/components/tab-group/tab-group.js';
import '@shoelace-style/shoelace/dist/components/tab/tab.js';
import '@shoelace-style/shoelace/dist/components/tab-panel/tab-panel.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';

/**
 * The Badge Colours settings page (renamed from State Colours 2026-09-04, when classification
 * types joined the map; file and element names keep the original identity): every workflow
 * state and classification chip the suite renders, with the badge each one wears, previewed in
 * light and dark AT ONCE.
 *
 * Design: documentation/engineering/state-colour-standard.md (D-040 option (c)); the layout is
 * the architect-approved 2026-09-03 mock. The page exists so the palette is chosen by looking
 * at it rather than by reading hex codes, and so an unmapped or badly grouped state is seen
 * here before a user meets it on a real screen.
 *
 * Shown is the EFFECTIVE map: code defaults with any stored overrides applied. AGWA admins
 * (and only they - the 2026-09-04 ruling, enforced server-side and reflected here) get a
 * Change button per row opening the palette dialog, and a Reset on overridden rows. Saves go
 * through the slice B API with concurrency tokens; a conflict reloads the map and reports in
 * place rather than silently overwriting another admin's choice.
 *
 * Tab ids are route segments per the ui-tabbed-pages recipe, grouped the way the approved mock
 * groups them: shared labels first, then one tab per product area.
 */

type StateColoursTabId = 'labels' | 'products' | 'glass' | 'companies';

const DEFAULT_TAB: StateColoursTabId = 'labels';

/**
 * What a pick shows when its save is refused on its token a second time, after the page has
 * reloaded the map and retried once: the row changed again while saving. It names no one
 * (@PR-LOCK-008), and it asks for another pick rather than a refresh, which the page has done.
 */
export const STATE_COLOUR_CHANGED_AGAIN =
  'This changed again while you were saving it. The page has refreshed with the latest - pick again to confirm yours.';

interface TabDefinition {
  id: StateColoursTabId;
  label: string;
  icon: string;
  /** The registry domains this tab shows; empty for the shared-labels tab. */
  domains: StateDomainKey[];
}

const AUTHORED_TABS: TabDefinition[] = [
  { id: 'labels', label: 'Shared Labels', icon: 'tags', domains: [] },
  {
    id: 'products',
    label: terms.product.plural,
    icon: 'boxes',
    domains: [
      'productWorkflow',
      'productCompliance',
      'productReport',
      'reportType',
      'airInfiltration',
      // Worn on a product or report whose company's register is inactive (D-146).
      'productRegister',
    ],
  },
  {
    id: 'glass',
    label: 'Glass',
    icon: 'award',
    domains: [
      'certificate',
      'certificateType',
      'certificateMarker',
      'as2208Rule',
      'as4666Product',
    ],
  },
  {
    id: 'companies',
    label: terms.loginGroup.plural,
    icon: 'building',
    domains: [
      'companyOperating',
      'membership',
      'siteOperating',
      'siteOperationType',
      'staffAccess',
      'memberDomainScope',
    ],
  },
];

/**
 * The tabs the page shows: a retired module's domains are dropped from every tab, and a tab left
 * with no domain is not shown at all (D-175), so retiring a module is one entry in
 * RETIRED_STATE_DOMAINS rather than an edit here. The shared-labels tab has no domains of its own
 * and always shows.
 */
const TABS: TabDefinition[] = AUTHORED_TABS.map((tab) => ({
  ...tab,
  domains: tab.domains.filter(
    (domain) => !RETIRED_STATE_DOMAINS.includes(domain)
  ),
})).filter((tab) => tab.id === 'labels' || tab.domains.length > 0);

/** Clamp whatever the URL carried to a real tab id; unknown segments land on the default. */
function toTabId(value: string | undefined): StateColoursTabId {
  return (TABS.find((tab) => tab.id === value)?.id ??
    DEFAULT_TAB) as StateColoursTabId;
}

@customElement('state-colours-page')
export class StateColoursPage extends LitElement {
  static styles = unsafeCSS(styles);

  /** Bound from the route's :tab? segment. */
  @property() tab?: string;

  @state() private activeTab: StateColoursTabId = DEFAULT_TAB;

  /** A failed direct action (reset conflict, network) shown above the tabs. */
  @state() private actionError = '';

  private unsubscribeOverrides?: () => void;

  /** Editing is AGWA-admin-only (2026-09-04 ruling); everyone else gets the viewer. */
  private get canEdit(): boolean {
    return activeUserProfile()?.access?.hasRole(CommonRoleId.admin) ?? false;
  }

  private get picker(): StateColourPickerDialog | null {
    return this.renderRoot.querySelector('state-colour-picker-dialog');
  }

  connectedCallback(): void {
    super.connectedCallback();
    ensureStateColourOverridesLoaded();
    this.unsubscribeOverrides = subscribeStateColourOverrides(() =>
      this.requestUpdate()
    );
  }

  disconnectedCallback(): void {
    this.unsubscribeOverrides?.();
    this.unsubscribeOverrides = undefined;
    super.disconnectedCallback();
  }

  /**
   * One attempt at persisting a pick, reading the CURRENT store state for tokens each time
   * it runs - which is what makes the conflict retry below meaningful. Picking the colour a
   * target would have WITHOUT its override is treated as a reset: the row returns to
   * following its default rather than pinning an override that happens to match it.
   * Returns null for the pick-the-default-with-no-override case, where there is nothing to
   * send.
   */
  private async saveColourOnce(
    target: StateColourPickTarget,
    colour: StatePaletteColour
  ): Promise<{ success: boolean; status: number; error?: string } | null> {
    const api = getStateColoursApi();
    if (target.kind === 'label') {
      const labelKey = target.labelKey!;
      const row = getLabelOverrideRow(labelKey);
      const codeDefault = STATE_LABELS[labelKey as StateLabelKey].colour;
      if (colour === codeDefault) {
        if (!row) return null;
        const reset = await api.resetLabelOverride(
          labelKey,
          row.concurrencyToken
        );
        if (reset.success) setStateColourOverrides(reset.data.overrides);
        return reset;
      }
      const set = await api.setLabelOverride(
        labelKey,
        colour,
        row?.concurrencyToken ?? null
      );
      if (set.success) await reloadStateColourOverrides();
      return set;
    }

    const { domainKey, valueKey } = target as {
      domainKey: string;
      valueKey: string;
    };
    const row = getUseOverrideRow(domainKey, valueKey);
    // Without its own override, a use follows its label (which may itself be overridden).
    const labelKey =
      STATE_DOMAINS[domainKey as StateDomainKey].states[valueKey];
    const labelDefault =
      getLabelOverrideColour(labelKey) ?? STATE_LABELS[labelKey].colour;
    if (colour === labelDefault) {
      if (!row) return null;
      const reset = await api.resetUseOverride(
        domainKey,
        valueKey,
        row.concurrencyToken
      );
      if (reset.success) setStateColourOverrides(reset.data.overrides);
      return reset;
    }
    const set = await api.setUseOverride(
      domainKey,
      valueKey,
      colour,
      row?.concurrencyToken ?? null
    );
    if (set.success) await reloadStateColourOverrides();
    return set;
  }

  /**
   * The save path behind every pick, with self-healing concurrency: a 409 means our token
   * view and the server's rows disagree - another admin's change, or a stale client. The
   * page is the machine here, so it refreshes its own map and retries ONCE with the fresh
   * tokens instead of asking a human to reload (the first cut did exactly that, and the
   * architect rightly called the message wrong). A second 409 is a genuine live race, and
   * the admin re-picks against the refreshed map they can now see.
   */
  private async handleColourPicked(
    event: CustomEvent<{
      target: StateColourPickTarget;
      colour: StatePaletteColour;
    }>
  ) {
    const { target, colour } = event.detail;
    const dialog = this.picker;
    if (!dialog) return;

    dialog.saving = true;
    this.actionError = '';
    try {
      let result = await this.saveColourOnce(target, colour);
      if (result && !result.success && result.status === 409) {
        await reloadStateColourOverrides();
        result = await this.saveColourOnce(target, colour);
      }
      if (result && !result.success) {
        // A second conflict says only that the row changed again while saving, never who changed
        // it (@PR-LOCK-008: a token cannot know, and it may have been this admin in another tab).
        // The page's own sentence rather than the server's, because the page has already
        // refreshed itself and the server's advice to refresh would be wrong here.
        throw new Error(
          result.status === 409
            ? STATE_COLOUR_CHANGED_AGAIN
            : result.error || 'Save failed.'
        );
      }
      dialog.hide();
    } catch (error) {
      // Leave the dialog open with the message, over a freshly-loaded map.
      dialog.errorMessage = (error as Error).message;
      void reloadStateColourOverrides();
    } finally {
      dialog.saving = false;
    }
  }

  private openLabelPicker(labelKey: StateLabelKey): void {
    this.picker?.show({
      kind: 'label',
      labelKey,
      displayLabel: STATE_LABELS[labelKey].label,
      currentColour:
        getLabelOverrideColour(labelKey) ?? STATE_LABELS[labelKey].colour,
    });
  }

  private openUsePicker(
    domain: StateDomainKey,
    value: string,
    labelKey: StateLabelKey
  ): void {
    this.picker?.show({
      kind: 'use',
      domainKey: domain,
      valueKey: value,
      displayLabel: `${STATE_LABELS[labelKey].label} (${STATE_DOMAINS[domain].title})`,
      currentColour: resolveState(domain, value).colour as StatePaletteColour,
    });
  }

  /** The per-row Reset control - only shown while an override exists. */
  private async resetOverride(target: StateColourPickTarget): Promise<void> {
    const api = getStateColoursApi();
    this.actionError = '';
    try {
      if (target.kind === 'label') {
        const row = getLabelOverrideRow(target.labelKey!);
        if (!row) return;
        const reset = await api.resetLabelOverride(
          target.labelKey!,
          row.concurrencyToken
        );
        if (!reset.success) throw new Error(reset.error || 'Reset failed.');
        setStateColourOverrides(reset.data!.overrides);
      } else {
        const row = getUseOverrideRow(target.domainKey!, target.valueKey!);
        if (!row) return;
        const reset = await api.resetUseOverride(
          target.domainKey!,
          target.valueKey!,
          row.concurrencyToken
        );
        if (!reset.success) throw new Error(reset.error || 'Reset failed.');
        setStateColourOverrides(reset.data!.overrides);
      }
    } catch (error) {
      this.actionError = (error as Error).message;
      void reloadStateColourOverrides();
    }
  }

  protected updated(changed: Map<string, unknown>) {
    super.updated(changed);
    if (changed.has('tab')) {
      this.activeTab = toTabId(this.tab);
      void this.showActiveTab();
    }
  }

  /**
   * Programmatically show the active tab: the initial [active] attribute alone is not reliably
   * picked up by sl-tab-group (the ui-tabbed-pages recipe, learned in product-registry-settings).
   */
  private async showActiveTab() {
    await this.updateComplete;
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const tabGroup = this.renderRoot.querySelector('sl-tab-group') as
      (HTMLElement & { show?: (name: string) => void }) | null;
    tabGroup?.show?.(this.activeTab);
  }

  /** Default tab keeps the bare URL so /settings/badge-colours is the canonical link. */
  private navigateToTab(tab: StateColoursTabId) {
    const suffix = tab === DEFAULT_TAB ? '' : `/${tab}`;
    void router?.navigate(resolveRouterPath(`settings/badge-colours${suffix}`));
  }

  private handleTabShow(event: CustomEvent<{ name: string }>) {
    const nextTab = toTabId(event.detail.name);
    if (nextTab === this.activeTab) return;
    this.activeTab = nextTab;
    this.navigateToTab(nextTab);
  }

  /**
   * The domains wearing a label, derived from the domain map so it cannot go stale. The
   * TITLES are shown beside each shared label so recolouring one is understood as
   * recolouring all of them; the COUNT decides where the label appears at all.
   */
  private domainsWearing(labelKey: StateLabelKey): string[] {
    // A retired module's domains are not counted (D-175): its title would otherwise appear beside
    // every label it shares, and a label it alone shared would read as shared.
    return (Object.keys(STATE_DOMAINS) as StateDomainKey[])
      .filter((domain) => !RETIRED_STATE_DOMAINS.includes(domain))
      .filter((domain) =>
        Object.values(STATE_DOMAINS[domain].states).includes(labelKey)
      )
      .map((domain) => STATE_DOMAINS[domain].title);
  }

  /**
   * A domain row's meta, in DISPLAY terms only (architect, 2026-09-04: no internal keys on
   * screen). A row wearing a shared label says where else that colour lands - the fact a
   * reader needs before recolouring - and a single-use row says nothing at all.
   */
  private sharedWithText(domain: StateDomainKey, labelKey: StateLabelKey) {
    const others = this.domainsWearing(labelKey).filter(
      (title) => title !== STATE_DOMAINS[domain].title
    );
    if (others.length === 0) return nothing;
    return html`shared with ${others.join(' · ')}`;
  }

  /**
   * Only labels worn in MORE THAN ONE place earn a row on the shared tab (architect,
   * 2026-09-04: "only things that map to more than one thing should be in the global list").
   * A single-use label is edited where it lives - its own tab's group - so the shared tab
   * reads as exactly the set of changes that fan out.
   */
  private get sharedLabelKeys(): StateLabelKey[] {
    return (Object.keys(STATE_LABELS) as StateLabelKey[]).filter(
      (labelKey) => this.domainsWearing(labelKey).length > 1
    );
  }

  /** The side-by-side theme preview: the same badge pinned to each authored face. */
  private renderPreviewPair(domain: StateDomainKey | undefined, value: string) {
    return html`
      <span class="pair">
        <span class="panel light" aria-label="Light theme preview">
          <agwa-state-badge
            .domain=${domain}
            state=${value}
            face="light"
          ></agwa-state-badge>
        </span>
        <span class="panel dark" aria-label="Dark theme preview">
          <agwa-state-badge
            .domain=${domain}
            state=${value}
            face="dark"
          ></agwa-state-badge>
        </span>
      </span>
    `;
  }

  /**
   * The Change / Reset controls on a row. Rendered only for AGWA admins - the server enforces
   * the same rule, so hiding them is honesty about capability, not the security boundary.
   */
  private renderRowActions(
    target: StateColourPickTarget,
    openPicker: () => void,
    hasOverride: boolean
  ) {
    if (!this.canEdit) return nothing;
    // Reset is rendered on every row and GHOSTED when there is no override, so the actions
    // column keeps one width and an override appearing never shifts the row sideways.
    return html`
      <span class="row-actions">
        <sl-button size="small" @click=${openPicker}>Change</sl-button>
        <sl-button
          size="small"
          variant="text"
          class=${hasOverride ? '' : 'ghost'}
          title="Back to the default colour"
          @click=${() => this.resetOverride(target)}
          >Reset</sl-button
        >
      </span>
    `;
  }

  /**
   * Description + palette, folded into one collapsible legend and CLOSED by default
   * (architect, 2026-09-04: the banner already titles the page, and this is reference
   * material for the unsure reader, not a banner for the sure one - the member-legend
   * reasoning). Swatches carry inline REFERENCES to the palette tokens, mirroring
   * agwa-state-badge: values live only in tokens.css, and a new colour needs no CSS here.
   */
  private renderLegend() {
    return html`
      <agwa-section
        class="legend"
        headline="Legend"
        variant="subtle"
        dense
        .collapsible=${true}
        .open=${false}
      >
        <p class="head-note">
          One page for every badge the suite wears - workflow states and
          classification chips - and its colour in both themes at once. A value
          that reaches a screen without an entry here renders the dashed
          unmapped look rather than borrowing a colour. The palette below is the
          whole range a badge may use; each colour is one choice, authored for
          light and dark together.
        </p>
        <div class="families">
          ${STATE_PALETTE_FAMILIES.map(
            ({ family, colours }) => html`
              <div class="family">
                <span class="family-name">${family}</span>
                <div class="chips">
                  ${colours.map(
                    (colour) => html`
                      <span class="chip">
                        <span class="duo">
                          <i
                            class="sw"
                            style="--sw-fill: var(--agwa-state-${colour}-light)"
                          ></i>
                          <i
                            class="sw"
                            style="--sw-fill: var(--agwa-state-${colour}-dark)"
                          ></i>
                        </span>
                        <span class="chip-name">${colour}</span>
                      </span>
                    `
                  )}
                </div>
              </div>
            `
          )}
        </div>
      </agwa-section>
    `;
  }

  private renderLabelsTab() {
    return html`
      <div class="group">
        <div class="group-head">
          Shared labels
          <span class="group-source"
            >worn in more than one place - changing one changes them all</span
          >
        </div>
        ${this.sharedLabelKeys.map((labelKey) => {
          const overridden = getLabelOverrideColour(labelKey);
          const effective = overridden ?? STATE_LABELS[labelKey].colour;
          return html`
            <div class="row" data-label-key=${labelKey}>
              <span class="row-name">
                ${STATE_LABELS[labelKey].label}
                ${
                  overridden
                    ? html`<span class="ov" title="Changed from the default"
                        >override</span
                      >`
                    : nothing
                }
              </span>
              <span class="row-meta"
                >${this.domainsWearing(labelKey).join(' · ')}</span
              >
              ${this.renderPreviewPair(undefined, labelKey)}
              <span class="row-colour">${effective}</span>
              ${this.renderRowActions(
                {
                  kind: 'label',
                  labelKey,
                  displayLabel: STATE_LABELS[labelKey].label,
                  currentColour: effective,
                },
                () => this.openLabelPicker(labelKey),
                !!overridden
              )}
            </div>
          `;
        })}
      </div>
    `;
  }

  private renderDomainGroup(domain: StateDomainKey) {
    const definition = STATE_DOMAINS[domain];
    return html`
      <div class="group" data-domain=${domain}>
        <div class="group-head">${definition.title}</div>
        ${Object.entries(definition.states).map(([value, labelKey]) => {
          const useOverridden = !!getUseOverrideRow(domain, value);
          const effective = resolveState(domain, value)
            .colour as StatePaletteColour;
          return html`
            <div class="row" data-state=${value}>
              <span class="row-name">
                ${STATE_LABELS[labelKey].label}
                ${
                  useOverridden
                    ? html`<span class="ov" title="Changed for this use only"
                        >override</span
                      >`
                    : nothing
                }
              </span>
              <span class="row-meta"
                >${this.sharedWithText(domain, labelKey)}</span
              >
              ${this.renderPreviewPair(domain, value)}
              <span class="row-colour">${effective}</span>
              ${this.renderRowActions(
                {
                  kind: 'use',
                  domainKey: domain,
                  valueKey: value,
                  displayLabel: STATE_LABELS[labelKey].label,
                  currentColour: effective,
                },
                () => this.openUsePicker(domain, value, labelKey),
                useOverridden
              )}
            </div>
          `;
        })}
      </div>
    `;
  }

  render() {
    return html`
      <div class="page">
        ${this.renderLegend()}
        ${
          this.actionError
            ? html`<p class="action-error" role="alert">${this.actionError}</p>`
            : nothing
        }
        <sl-tab-group @sl-tab-show=${this.handleTabShow}>
          ${TABS.map(
            (tab) => html`
              <sl-tab
                slot="nav"
                panel=${tab.id}
                ?active=${tab.id === this.activeTab}
              >
                <sl-icon name=${tab.icon} class="tab-icon"></sl-icon>
                ${tab.label}
              </sl-tab>
            `
          )}
          ${TABS.map(
            (tab) => html`
              <sl-tab-panel name=${tab.id}>
                ${
                  tab.id === 'labels'
                    ? this.renderLabelsTab()
                    : tab.domains.map((domain) =>
                        this.renderDomainGroup(domain)
                      )
                }
              </sl-tab-panel>
            `
          )}
        </sl-tab-group>
        <state-colour-picker-dialog
          @colour-picked=${this.handleColourPicked}
        ></state-colour-picker-dialog>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'state-colours-page': StateColoursPage;
  }
}
